import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Page, Spinner } from "../../components";
import LogoutButton from "../../components/LogoutButton";
import {
  useAdminIssueCategories,
  useAdminIssueCategoriesFiltered,
  useAdminCategoryHistory,
} from "../../api/hooks/admin";
import AdminController from "../../controllers/AdminController";
import { toLocalDateString } from "../../utils/date";

const COLORS = [
  "#1b3bcc",
  "#10b981",
  "#f5a623",
  "#e74c3c",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#f97316",
  "#14b8a6",
  "#a855f7",
  "#ef4444",
];

const IssueTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: categories, isLoading: categoriesLoading } =
    useAdminIssueCategories();
  const { data: history, isLoading: historyLoading } =
    useAdminCategoryHistory();

  const [collectLoading, setCollectLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const [showHistory, setShowHistory] = useState(false);
  const [actionResult, setActionResult] = useState<string | null>(null);

  // Date range state — default to past 6 months
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return toLocalDateString(d);
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return toLocalDateString(new Date());
  });

  // Shared hover state for cross-chart highlighting
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  const { data: filteredCategories, isLoading: filteredLoading } =
    useAdminIssueCategoriesFiltered(
      startDate || undefined,
      endDate || undefined
    );

  const hasDateRange = !!startDate && !!endDate;

  // Selected categories for distribution chart
  const [selectedDistCategories, setSelectedDistCategories] = useState<Set<string>>(new Set());

  const sortedCategories = useMemo(() => {
    if (!categories) return [];
    return [...categories].sort((a, b) => b.issue_count - a.issue_count);
  }, [categories]);

  const pieData = useMemo(() => {
    return sortedCategories.map((c) => ({
      name: c.name,
      value: c.issue_count,
    }));
  }, [sortedCategories]);

  // Build trend chart data from history
  const trendData = useMemo(() => {
    if (!history || history.length === 0) return [];
    const allCategoryNames = new Set<string>();
    history.forEach((h) =>
      h.categories.forEach((c) => allCategoryNames.add(c.name))
    );
    return [...history]
      .sort((a, b) => a.version - b.version)
      .map((h) => {
        const point: Record<string, any> = { version: `v${h.version}` };
        allCategoryNames.forEach((name) => {
          const cat = h.categories.find((c) => c.name === name);
          point[name] = cat ? cat.issue_count : 0;
        });
        return point;
      });
  }, [history]);

  const trendCategoryNames = useMemo(() => {
    if (!history) return [];
    const names = new Set<string>();
    history.forEach((h) => h.categories.forEach((c) => names.add(c.name)));
    return [...names];
  }, [history]);

  // Current active category names for highlighting removed ones in history
  const activeCategoryNames = useMemo(() => {
    return new Set(sortedCategories.map((c) => c.name));
  }, [sortedCategories]);

  // Distribution chart data from filtered categories buckets
  const distributionData = useMemo(() => {
    if (!filteredCategories) return [];
    const catsToShow = selectedDistCategories.size > 0
      ? filteredCategories.filter((c) => selectedDistCategories.has(c.name))
      : filteredCategories;
    const monthMap = new Map<string, Record<string, any>>();
    catsToShow.forEach((cat) => {
      (cat.buckets || []).forEach((bucket) => {
        if (!monthMap.has(bucket.month)) {
          monthMap.set(bucket.month, { month: bucket.month });
        }
        monthMap.get(bucket.month)![cat.name] = bucket.count;
      });
    });
    return [...monthMap.values()].sort((a, b) =>
      a.month.localeCompare(b.month)
    );
  }, [filteredCategories, selectedDistCategories]);

  const distributionCategoryNames = useMemo(() => {
    if (!filteredCategories) return [];
    if (selectedDistCategories.size > 0) {
      return filteredCategories
        .filter((c) => selectedDistCategories.has(c.name))
        .map((c) => c.name);
    }
    return filteredCategories.map((c) => c.name);
  }, [filteredCategories, selectedDistCategories]);

  // Completion time line chart data
  const completionTimeData = useMemo(() => {
    if (!filteredCategories) return [];
    const monthMap = new Map<string, Record<string, any>>();
    filteredCategories.forEach((cat) => {
      (cat.buckets || []).forEach((bucket) => {
        if (bucket.avg_completion_time_days == null) return;
        if (!monthMap.has(bucket.month)) {
          monthMap.set(bucket.month, { month: bucket.month });
        }
        monthMap.get(bucket.month)![cat.name] = bucket.avg_completion_time_days;
      });
    });
    return [...monthMap.values()].sort((a, b) =>
      a.month.localeCompare(b.month)
    );
  }, [filteredCategories]);

  const filteredCategoryNames = useMemo(() => {
    if (!filteredCategories) return [];
    return filteredCategories.map((c) => c.name);
  }, [filteredCategories]);

  // Category summary table data
  const summaryTableData = useMemo(() => {
    if (!filteredCategories) return [];
    return filteredCategories
      .map((cat) => {
        const buckets = cat.buckets || [];
        const totalIssues = buckets.reduce((sum, b) => sum + b.count, 0);
        const avgCompletion = cat.avg_completion_time_days;
        const sortedBuckets = [...buckets].sort((a, b) =>
          a.month.localeCompare(b.month)
        );
        const firstCount = sortedBuckets.length > 0 ? sortedBuckets[0].count : 0;
        const lastCount =
          sortedBuckets.length > 0
            ? sortedBuckets[sortedBuckets.length - 1].count
            : 0;
        let trendPct: number | null = null;
        if (firstCount > 0) {
          trendPct = ((lastCount - firstCount) / firstCount) * 100;
        }
        return {
          name: cat.name,
          totalIssues,
          avgCompletion,
          trendPct,
        };
      })
      .sort((a, b) => b.totalIssues - a.totalIssues);
  }, [filteredCategories]);

  const toggleCategory = (name: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleCollect = async () => {
    setCollectLoading(true);
    setActionResult(null);
    try {
      const result = await AdminController.collectIssues();
      setActionResult(
        `Fetched ${result.total_fetched} issues, ${result.total_new} new, ${result.classified} classified, ${result.slotted_into_categories} slotted into categories.`
      );
      queryClient.invalidateQueries("admin-issue-categories");
      queryClient.invalidateQueries("admin-category-history");
    } catch (e: any) {
      setActionResult(`Error: ${e.message || "Failed to collect issues"}`);
    } finally {
      setCollectLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalcLoading(true);
    setActionResult(null);
    try {
      const result = await AdminController.recalculateCategories();
      setActionResult(
        result.message ||
          `Recalculated ${result.categories?.length || 0} categories.`
      );
      queryClient.invalidateQueries("admin-issue-categories");
      queryClient.invalidateQueries("admin-category-history");
    } catch (e: any) {
      setActionResult(
        `Error: ${e.message || "Failed to recalculate categories"}`
      );
    } finally {
      setRecalcLoading(false);
    }
  };

  const anyLoading = collectLoading || recalcLoading;

  return (
    <Page>
      {/* Header */}
      <div
        style={{
          width: "100%",
          maxWidth: "80vw",
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 16px",
          boxSizing: "border-box" as const,
          flexWrap: "wrap" as const,
          gap: 8,
          margin: "0 auto 24px",
        }}
      >
        <h1
          style={{
            fontSize: "clamp(32px, 6vw, 49px)",
            margin: 0,
            flex: "1 1 auto",
            minWidth: 0,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Issue Tracking
        </h1>
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap" as const,
          }}
        >
          <button
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              background: "#6b7280",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "background-color 0.2s ease",
            }}
            onClick={() => navigate("/")}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#4b5563";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#6b7280";
            }}
          >
            Back to Home
          </button>
          <LogoutButton />
        </div>
      </div>

      {/* Action Buttons Bar */}
      <div
        style={{
          width: "100%",
          maxWidth: "80vw",
          margin: "0 auto 24px",
          padding: 20,
          border: "1px solid #e0e0e0",
          borderRadius: 8,
          backgroundColor: "#f9fafb",
          boxSizing: "border-box" as const,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap" as const,
          }}
        >
          <button
            onClick={handleCollect}
            disabled={anyLoading}
            style={{
              padding: "10px 20px",
              borderRadius: 6,
              border: "none",
              background: anyLoading ? "#9ca3af" : "#1b3bcc",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: anyLoading ? "not-allowed" : "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            {collectLoading ? "Processing..." : "Process New Tickets"}
          </button>
          <button
            onClick={handleRecalculate}
            disabled={anyLoading}
            style={{
              padding: "10px 20px",
              borderRadius: 6,
              border: "none",
              background: anyLoading ? "#9ca3af" : "#8b5cf6",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: anyLoading ? "not-allowed" : "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            {recalcLoading ? "Recalculating..." : "Recalculate All Categories"}
          </button>
        </div>
        {actionResult && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 14px",
              background: actionResult.startsWith("Error") ? "#fef2f2" : "#f0fdf4",
              border: `1px solid ${actionResult.startsWith("Error") ? "#fecaca" : "#bbf7d0"}`,
              borderRadius: 6,
              fontSize: 14,
              color: actionResult.startsWith("Error") ? "#991b1b" : "#166534",
            }}
          >
            {actionResult}
          </div>
        )}
      </div>

      {/* Content */}
      {categoriesLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 32,
          }}
        >
          <Spinner />
        </div>
      ) : !sortedCategories.length ? (
        <div
          style={{
            width: "100%",
            maxWidth: "80vw",
            margin: "0 auto",
            textAlign: "center",
            padding: 40,
            color: "#666",
            fontSize: 16,
          }}
        >
          No issue categories found. Try processing new tickets first.
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            maxWidth: "80vw",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Pie Chart */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: 18,
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Issue Breakdown by Category
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={140}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, value }) => `${name} (${value})`}
                  labelLine
                >
                  {pieData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Categories List */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Categories ({sortedCategories.length})
            </h3>
            {sortedCategories.map((cat) => {
              const isExpanded = expandedCategories.has(cat.name);
              const hasCompletionTime =
                cat.avg_completion_time_days != null;
              return (
                <div
                  key={cat.name}
                  onMouseEnter={() => setHoveredCategory(cat.name)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  style={{
                    border:
                      hoveredCategory === cat.name
                        ? "1px solid #6b7280"
                        : "1px solid #e5e7eb",
                    borderRadius: 12,
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                    transition: "border-color 0.15s ease",
                  }}
                >
                  <div
                    onClick={() => toggleCategory(cat.name)}
                    style={{
                      padding: "16px 20px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          color: "#1a1a1a",
                        }}
                      >
                        {cat.name}
                      </span>
                      <span
                        style={{
                          background: "#e0e7ff",
                          color: "#3730a3",
                          padding: "2px 10px",
                          borderRadius: 12,
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        {cat.issue_count}
                      </span>
                      {hasCompletionTime && (
                        <span
                          style={{
                            background: "#dcfce7",
                            color: "#166534",
                            padding: "2px 10px",
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          ~{cat.avg_completion_time_days!.toFixed(1)}d avg
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: 18,
                        color: "#6b7280",
                        transition: "transform 0.2s",
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ▼
                    </span>
                  </div>
                  {isExpanded && (
                    <div
                      style={{
                        padding: "0 20px 20px",
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      {cat.description && (
                        <p
                          style={{
                            margin: "12px 0 4px",
                            fontSize: 14,
                            color: "#374151",
                          }}
                        >
                          <strong>Description:</strong> {cat.description}
                        </p>
                      )}
                      {cat.reason && (
                        <p
                          style={{
                            margin: "4px 0 12px",
                            fontSize: 14,
                            color: "#374151",
                          }}
                        >
                          <strong>Reason:</strong> {cat.reason}
                        </p>
                      )}
                      {cat.issues && cat.issues.length > 0 && (
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: 14,
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                borderBottom: "2px solid #e5e7eb",
                                textAlign: "left",
                              }}
                            >
                              <th
                                style={{
                                  padding: "8px 12px",
                                  color: "#374151",
                                }}
                              >
                                Identifier
                              </th>
                              <th
                                style={{
                                  padding: "8px 12px",
                                  color: "#374151",
                                }}
                              >
                                Title
                              </th>
                              {cat.issues.some(
                                (i) => i.completion_time_days != null
                              ) && (
                                <th
                                  style={{
                                    padding: "8px 12px",
                                    color: "#374151",
                                  }}
                                >
                                  Completion Time
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {cat.issues.map((issue) => (
                              <tr
                                key={issue.identifier}
                                style={{
                                  borderBottom: "1px solid #f0f0f0",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "8px 12px",
                                    fontFamily: "monospace",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {issue.identifier}
                                </td>
                                <td style={{ padding: "8px 12px" }}>
                                  {issue.url ? (
                                    <a
                                      href={issue.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: "#1b3bcc",
                                        textDecoration: "none",
                                      }}
                                    >
                                      {issue.title}
                                    </a>
                                  ) : (
                                    issue.title
                                  )}
                                </td>
                                {cat.issues.some(
                                  (i) => i.completion_time_days != null
                                ) && (
                                  <td
                                    style={{
                                      padding: "8px 12px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {issue.completion_time_days != null
                                      ? `${issue.completion_time_days.toFixed(1)}d`
                                      : "—"}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Date Range Selector */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 20,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: 18,
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Time-Based Analysis
            </h3>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap" as const,
              }}
            >
              <label
                style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}
              >
                Start Date
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    marginLeft: 8,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    fontSize: 14,
                  }}
                />
              </label>
              <label
                style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}
              >
                End Date
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    marginLeft: 8,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    fontSize: 14,
                  }}
                />
              </label>
              {(startDate || endDate) && (
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    color: "#374151",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              )}
              {filteredLoading && (
                <Spinner />
              )}
            </div>
          </div>

          {/* Time-Based Charts (only when date range is set) */}
          {hasDateRange && filteredCategories && (
            <>
              {/* Stacked Area Chart */}
              {distributionData.length > 0 && (
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 24,
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: 18,
                      fontWeight: 600,
                      color: "#1a1a1a",
                    }}
                  >
                    Category Distribution Over Time
                  </h3>
                  {/* Category filter pills */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap" as const,
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    <button
                      onClick={() => setSelectedDistCategories(new Set())}
                      style={{
                        padding: "4px 12px",
                        borderRadius: 16,
                        border: "1px solid #d1d5db",
                        background: selectedDistCategories.size === 0 ? "#1a1a1a" : "#fff",
                        color: selectedDistCategories.size === 0 ? "#fff" : "#374151",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      All
                    </button>
                    {filteredCategoryNames.map((name, i) => {
                      const isSelected = selectedDistCategories.has(name);
                      const color = COLORS[i % COLORS.length];
                      return (
                        <button
                          key={name}
                          onClick={() => {
                            setSelectedDistCategories((prev) => {
                              const next = new Set(prev);
                              if (next.has(name)) next.delete(name);
                              else next.add(name);
                              return next;
                            });
                          }}
                          onMouseEnter={() => setHoveredCategory(name)}
                          onMouseLeave={() => setHoveredCategory(null)}
                          style={{
                            padding: "4px 12px",
                            borderRadius: 16,
                            border: `1px solid ${isSelected ? color : "#d1d5db"}`,
                            background: isSelected ? color : "#fff",
                            color: isSelected ? "#fff" : "#374151",
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={distributionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend
                        onMouseEnter={(e: any) => setHoveredCategory(e.value)}
                        onMouseLeave={() => setHoveredCategory(null)}
                      />
                      {distributionCategoryNames.map((name, i) => {
                        const colorIdx = filteredCategoryNames.indexOf(name);
                        const dimmed = hoveredCategory !== null && hoveredCategory !== name;
                        return (
                          <Line
                            key={name}
                            type="monotone"
                            dataKey={name}
                            stroke={COLORS[(colorIdx >= 0 ? colorIdx : i) % COLORS.length]}
                            strokeWidth={hoveredCategory === name ? 4 : 2}
                            strokeOpacity={dimmed ? 0.15 : 1}
                            dot={{ r: hoveredCategory === name ? 5 : 3 }}
                            connectNulls
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Completion Time Line Chart */}
              {completionTimeData.length > 0 && (
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 24,
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 16px 0",
                      fontSize: 18,
                      fontWeight: 600,
                      color: "#1a1a1a",
                    }}
                  >
                    Completion Time Trends
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={completionTimeData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis
                        allowDecimals={false}
                        label={{
                          value: "Days",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip />
                      <Legend
                        onMouseEnter={(e: any) => setHoveredCategory(e.value)}
                        onMouseLeave={() => setHoveredCategory(null)}
                      />
                      {filteredCategoryNames.map((name, i) => {
                        const dimmed = hoveredCategory !== null && hoveredCategory !== name;
                        return (
                          <Line
                            key={name}
                            type="monotone"
                            dataKey={name}
                            stroke={COLORS[i % COLORS.length]}
                            strokeWidth={hoveredCategory === name ? 4 : 2}
                            strokeOpacity={dimmed ? 0.15 : 1}
                            dot={{ r: hoveredCategory === name ? 5 : 3 }}
                            connectNulls
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Category Summary Table */}
              {summaryTableData.length > 0 && (
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 24,
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 16px 0",
                      fontSize: 18,
                      fontWeight: 600,
                      color: "#1a1a1a",
                    }}
                  >
                    Category Summary
                  </h3>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 14,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: "2px solid #e5e7eb",
                          textAlign: "left",
                        }}
                      >
                        <th style={{ padding: "8px 12px", color: "#374151" }}>
                          Category
                        </th>
                        <th style={{ padding: "8px 12px", color: "#374151" }}>
                          Total Issues
                        </th>
                        <th style={{ padding: "8px 12px", color: "#374151" }}>
                          Avg Completion Time
                        </th>
                        <th style={{ padding: "8px 12px", color: "#374151" }}>
                          Trend
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryTableData.map((row) => (
                        <tr
                          key={row.name}
                          onMouseEnter={() => setHoveredCategory(row.name)}
                          onMouseLeave={() => setHoveredCategory(null)}
                          style={{
                            borderBottom: "1px solid #f0f0f0",
                            cursor: "default",
                            background:
                              hoveredCategory === row.name
                                ? "#f3f4f6"
                                : "transparent",
                            transition: "background 0.15s ease",
                          }}
                        >
                          <td
                            style={{
                              padding: "8px 12px",
                              fontWeight: 500,
                            }}
                          >
                            {row.name}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            {row.totalIssues}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            {row.avgCompletion != null
                              ? `${row.avgCompletion.toFixed(1)}d`
                              : "—"}
                          </td>
                          <td style={{ padding: "8px 12px" }}>
                            {row.trendPct != null ? (
                              <span
                                style={{
                                  color:
                                    row.trendPct > 0
                                      ? "#dc2626"
                                      : row.trendPct < 0
                                        ? "#16a34a"
                                        : "#6b7280",
                                  fontWeight: 500,
                                }}
                              >
                                {row.trendPct > 0
                                  ? `▲ +${row.trendPct.toFixed(0)}%`
                                  : row.trendPct < 0
                                    ? `▼ ${row.trendPct.toFixed(0)}%`
                                    : "— 0%"}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Category History */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              marginBottom: 40,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: showHistory ? 16 : 0,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                Category History
              </h3>
              <button
                onClick={() => setShowHistory(!showHistory)}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  fontWeight: 500,
                  border: "1px solid #e0e0e0",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: showHistory ? "#3949ab" : "#fff",
                  color: showHistory ? "#fff" : "#374151",
                  transition: "all 0.15s ease",
                }}
              >
                {showHistory ? "Hide" : "Show"}
              </button>
            </div>
            {showHistory && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                }}
              >
                {/* Trend Chart */}
                {trendData.length > 1 && (
                  <div>
                    <h4
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1a1a1a",
                      }}
                    >
                      Category Trends Across Versions
                    </h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="version" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        {trendCategoryNames.map((name, i) => (
                          <Line
                            key={name}
                            type="monotone"
                            dataKey={name}
                            stroke={COLORS[i % COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Version List */}
                {historyLoading ? (
                  <Spinner />
                ) : !history || history.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "#666",
                      fontSize: 14,
                    }}
                  >
                    No category history available.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {[...history]
                      .sort((a, b) => b.version - a.version)
                      .map((entry) => (
                        <div
                          key={entry.version}
                          style={{
                            padding: "14px 18px",
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            background: entry.is_active ? "#f0fdf4" : "#f9fafb",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              marginBottom: 8,
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: 15,
                                color: "#1a1a1a",
                              }}
                            >
                              Version {entry.version}
                            </span>
                            {entry.is_active && (
                              <span
                                style={{
                                  background: "#dcfce7",
                                  color: "#166534",
                                  padding: "2px 8px",
                                  borderRadius: 10,
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                Active
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: 12,
                                color: "#999",
                              }}
                            >
                              {entry.calculated_at
                                ? new Date(entry.calculated_at).toLocaleString()
                                : ""}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                            }}
                          >
                            {entry.categories.map((cat) => {
                              const isRemoved =
                                entry.is_active &&
                                !activeCategoryNames.has(cat.name);
                              return (
                                <div
                                  key={cat.name}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "4px 8px",
                                    fontSize: 14,
                                    color: isRemoved ? "#9ca3af" : "#374151",
                                    textDecoration: isRemoved
                                      ? "line-through"
                                      : "none",
                                  }}
                                >
                                  <span>{cat.name}</span>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 12,
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        color: "#6b7280",
                                      }}
                                    >
                                      {cat.issue_count} issues
                                    </span>
                                    {cat.edited_by && (
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#9ca3af",
                                        }}
                                      >
                                        by {cat.edited_by}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Page>
  );
};

export default IssueTrackingPage;
